/**
 * Navbar - Barre de navigation principale
 * 
 * Position fixe en haut, contient:
 * - Bouton menu (mobile)
 * - Logo à gauche
 * - Sélecteur de langue, mode nuit, déconnexion à droite
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { 
  Shield, 
  Sun, 
  Moon, 
  LogOut, 
  Globe, 
  ChevronDown,
  Wifi,
  WifiOff,
  Menu,
  Building2,
  User
} from 'lucide-react';
import api from '@/lib/api';

// Langues disponibles
const LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

export function Navbar({ onMenuClick, showMenuButton }) {
  const { t, i18n } = useTranslation();
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { connected, rooms } = useWebSocket();
  const [buildingNames, setBuildingNames] = useState([]);

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  // Resolve building room IDs to names
  useEffect(() => {
    const buildingRooms = (rooms || []).filter(r => r.startsWith('building_'));
    if (buildingRooms.length === 0) {
      setBuildingNames([]);
      return;
    }
    const ids = buildingRooms.map(r => r.replace('building_', ''));
    api.get('/buildings')
      .then(res => {
        const all = res.data || [];
        const names = ids.map(id => {
          const b = all.find(b => b.id === id);
          return b ? b.name : id.slice(0, 8);
        });
        setBuildingNames(names);
      })
      .catch(() => setBuildingNames(ids.map(id => id.slice(0, 8))));
  }, [rooms]);

  // Determine scope label for the badge
  const getScopeLabel = () => {
    if (!rooms || rooms.length === 0) return null;
    if (rooms.includes('admin_all')) return 'Tous les sites';
    const tenantRoom = rooms.find(r => r.startsWith('tenant_'));
    if (tenantRoom && !buildingNames.length) return 'Tout le tenant';
    if (buildingNames.length > 0) return buildingNames.join(', ');
    return null;
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('language', langCode);
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 h-14 bg-[#1E3A5F] border-b border-white/10 shadow-lg"
      data-testid="main-navbar"
    >
      <div className="h-full px-4 flex items-center justify-between">
        {/* Gauche: Menu burger + Logo */}
        <div className="flex items-center gap-2">
          {/* Bouton menu hamburger (mobile uniquement) */}
          {showMenuButton && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onMenuClick}
              className="h-9 w-9 text-white/80 hover:text-white hover:bg-white/10 lg:hidden"
              data-testid="navbar-menu-btn"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          
          <Link 
            to="/dashboard" 
            className="flex items-center gap-2 hover:opacity-90 transition-opacity"
            data-testid="navbar-logo"
          >
            <Shield className="h-7 w-7 text-[#06B6D4]" />
            <span className="font-bold text-lg text-white tracking-tight">
              OhmGuard
            </span>
          </Link>
        </div>

        {/* Actions à droite */}
        <div className="flex items-center gap-2">
          {/* Indicateur de connexion */}
          <div
            className={cn(
              'hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium mr-2',
              connected
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-red-500/20 text-red-400'
            )}
            data-testid="navbar-connection-status"
          >
            {connected ? (
              <>
                <Wifi className="h-3 w-3" />
                <span>Live</span>
              </>
            ) : (
              <>
                <WifiOff className="h-3 w-3" />
                <span>Offline</span>
              </>
            )}
          </div>

          {/* Sélecteur de langue */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="sm"
                className="h-9 px-2 text-white/80 hover:text-white hover:bg-white/10"
                data-testid="navbar-language-btn"
              >
                <Globe className="h-4 w-4 mr-1.5" />
                <span className="hidden sm:inline">{currentLang.flag} {currentLang.code.toUpperCase()}</span>
                <ChevronDown className="h-3 w-3 ml-1 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[140px]">
              {LANGUAGES.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={cn(
                    'cursor-pointer',
                    i18n.language === lang.code && 'bg-accent'
                  )}
                  data-testid={`navbar-lang-${lang.code}`}
                >
                  <span className="mr-2">{lang.flag}</span>
                  {lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Toggle Mode Nuit/Clair */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-9 w-9 text-white/80 hover:text-white hover:bg-white/10"
            data-testid="navbar-theme-toggle"
            aria-label={theme === 'dark' ? t('theme.light') : t('theme.dark')}
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </Button>

          {/* Séparateur */}
          <div className="h-6 w-px bg-white/20 mx-1" />

          {/* Info utilisateur + Scope + Déconnexion */}
          <div className="flex items-center gap-2">
            {/* Scope indicator */}
            {connected && getScopeLabel() && (
              <div 
                className="hidden lg:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/10 text-xs text-white/70"
                data-testid="navbar-scope-badge"
              >
                <Building2 className="h-3 w-3" />
                <span className="max-w-[180px] truncate">{getScopeLabel()}</span>
              </div>
            )}

            {/* Nom + rôle de l'utilisateur */}
            {user && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="h-9 px-2.5 text-white/80 hover:text-white hover:bg-white/10 gap-2"
                    data-testid="navbar-user-menu"
                  >
                    <div className="h-7 w-7 rounded-full bg-[#06B6D4]/30 flex items-center justify-center text-[#06B6D4] text-xs font-semibold">
                      {(user.full_name || user.email || '?').charAt(0).toUpperCase()}
                    </div>
                    <div className="hidden md:flex flex-col items-start leading-none">
                      <span className="text-xs font-medium text-white">
                        {user.full_name || user.email}
                      </span>
                      <span className="text-[10px] text-white/50">
                        {user.role?.replace('_', ' ')}
                      </span>
                    </div>
                    <ChevronDown className="h-3 w-3 opacity-50 hidden md:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col gap-1">
                      <p className="text-sm font-medium">{user.full_name || 'Utilisateur'}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                      <Badge variant="outline" className="w-fit text-[10px]">{user.role?.replace('_', ' ')}</Badge>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {connected && getScopeLabel() && (
                    <>
                      <DropdownMenuLabel className="text-xs text-muted-foreground font-normal flex items-center gap-1.5">
                        <Building2 className="h-3 w-3" />
                        Surveillance : {getScopeLabel()}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="navbar-logout-btn"
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    {t('auth.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
